/**
 * Calendar dates and server timestamps — the one place that knows which
 * clock a value is on (UI overhaul PR-3b; the rest of `lib/format` lands in PR-5).
 *
 * The bug family this closes (audit APP-18, SET-02, and the eight instances in
 * memory `clickpay-utc-local-day-boundary-bug-family`): a value captured on one
 * clock and evaluated on another. In the browser that was `new Date("2026-10-11")`
 * (a bare date parses as UTC midnight, so it renders as 10/10 after 8 pm
 * Eastern), `toISOString().slice(0, 10)` for "today" (the UTC date, so after
 * 8 pm Eastern it is already tomorrow), and ages measured against this
 * laptop's clock (68 s behind the mini, so a run that just finished read
 * "in 39s"). Three rules:
 *
 *   1. A date-only string `YYYY-MM-DD` is a calendar date on the ACCOUNT's day
 *      (Pacific, Amazon's day); `today` is the account's today, never the
 *      browser's. Parse one with `parseYMD`, build one with
 *      `isoYMD`/`todayYMD`, never `toISOString`.
 *   2. A timestamp without an offset came from the server and IS UTC (the
 *      backend convention, `time_windows.utc_now_naive`). Parse it with
 *      `parseServerTimestamp`.
 *   3. "How long ago" is measured against the SERVER's now (`lib/clock.ts`),
 *      and is never negative.
 */
import { serverNow } from '../clock'

const YMD = /^(\d{4})-(\d{2})-(\d{2})$/

/** Local calendar date for a `YYYY-MM-DD` string (local midnight). `null` for
 *  anything else — callers that might receive a full timestamp should use
 *  `parseServerTimestamp`. */
export function parseYMD(value: string | null | undefined): Date | null {
  if (!value) return null
  const m = YMD.exec(value)
  if (!m) return null
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  return Number.isNaN(d.getTime()) ? null : d
}

/** `YYYY-MM-DD` built from LOCAL year / 0-based month / day. `Date` normalises
 *  overflow and underflow (month -1 is December of the prior year, day 0 is the
 *  last day of the prior month), which every calendar helper relies on. */
export function isoYMD(y: number, m0: number, d: number): string {
  const dt = new Date(y, m0, d)
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`
}

/** `YYYY-MM-DD` of a local Date. */
export function toYMD(dt: Date): string {
  return isoYMD(dt.getFullYear(), dt.getMonth(), dt.getDate())
}

/** The account's time zone: Amazon's day (README: "Account day = America/Los_Angeles"). */
export const ACCOUNT_TZ = 'America/Los_Angeles'

/** Today's calendar date on the ACCOUNT's day (Pacific), whatever zone the
 *  browser is in. (`new Date().toISOString().slice(0, 10)` is the UTC date and
 *  the browser's local date is an hour-or-more off Amazon's from 9 pm Eastern.) */
export function accountTodayYMD(now: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: ACCOUNT_TZ }).format(now)
}

/** A Date at local midnight of the account's calendar day, so every Y/M/D
 *  helper (`isoYMD`, week / month / quarter maths) keeps working unchanged. */
export function accountNow(now: Date = new Date()): Date {
  return parseYMD(accountTodayYMD(now)) as Date
}

/** The zone's UTC offset (ms, east positive) at an instant. */
function accountOffsetMs(at: number): number {
  const p = new Intl.DateTimeFormat('en-US', {
    timeZone: ACCOUNT_TZ, hourCycle: 'h23', year: 'numeric', month: 'numeric', day: 'numeric',
    hour: 'numeric', minute: 'numeric', second: 'numeric',
  }).formatToParts(new Date(at))
  const n = (t: string) => Number(p.find(x => x.type === t)?.value)
  return Date.UTC(n('year'), n('month') - 1, n('day'), n('hour'), n('minute'), n('second')) - Math.floor(at / 1000) * 1000
}

/** The instant a `YYYY-MM-DD` account (Pacific) day STARTS, whatever zone the
 *  browser is in. `null` when `ymd` is not a date. A feed filtered on instants
 *  must cut at this, not at the browser's midnight. */
export function accountDayStart(ymd: string): Date | null {
  const m = YMD.exec(ymd)
  if (!m) return null
  const wall = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  // Two passes so a day whose midnight is on the other side of a DST change still lands right.
  const first = wall - accountOffsetMs(wall + 8 * 3_600_000)
  return new Date(wall - accountOffsetMs(first))
}

/** Today's calendar date on the account's day (Pacific). */
export function todayYMD(now: Date = new Date()): string {
  return accountTodayYMD(now)
}

/** `days` before today, on the account's calendar. */
export function daysAgoYMD(days: number, now: Date = new Date()): string {
  const a = accountNow(now)
  return isoYMD(a.getFullYear(), a.getMonth(), a.getDate() - days)
}

/** Shift a `YYYY-MM-DD` by `days` (negative allowed), on the local calendar. */
export function addDaysYMD(ymd: string, days: number): string {
  const d = parseYMD(ymd)
  if (!d) return ymd
  return isoYMD(d.getFullYear(), d.getMonth(), d.getDate() + days)
}

/** Whole calendar days from `a` to `b` (b − a); 0 when either is malformed. */
export function diffDaysYMD(a: string, b: string): number {
  const da = parseYMD(a)
  const db = parseYMD(b)
  if (!da || !db) return 0
  return Math.round((db.getTime() - da.getTime()) / 86_400_000)
}

/** Lexicographic `YYYY-MM-DD` comparison is chronological — this exists so the
 *  intent reads at the call site. */
export function minYMD(a: string, b: string): string {
  return a < b ? a : b
}

/**
 * A timestamp from the API as a Date:
 *  - `YYYY-MM-DD` → local calendar date (midnight local);
 *  - `YYYY-MM-DDTHH:MM[:SS[.fff]]` with no offset → UTC (server convention);
 *  - anything with `Z` or an offset → as written.
 * `null` when unparseable.
 */
export function parseServerTimestamp(value: string | null | undefined): Date | null {
  if (!value) return null
  const dateOnly = parseYMD(value)
  if (dateOnly) return dateOnly
  const hasOffset = /(Z|[+-]\d{2}:?\d{2})$/i.test(value)
  const t = Date.parse(hasOffset ? value : `${value}Z`)
  if (Number.isNaN(t)) return null
  return new Date(t)
}

/** Hours since `value` on the server's clock; never negative (a stamp that
 *  reads as the future is "just now" — clocks disagree by seconds, data does
 *  not arrive from the future). `null` when unparseable. */
export function ageHoursSince(value: string | null | undefined, nowMs: number = serverNow()): number | null {
  const dt = parseServerTimestamp(value)
  if (!dt) return null
  return Math.max(0, (nowMs - dt.getTime()) / 3_600_000)
}

/** "Sep 16" for a `YYYY-MM-DD` (local calendar, never shifts a day). */
export function fmtMonthDay(ymd: string | null | undefined): string {
  const d = parseYMD(ymd)
  if (!d) return '—'
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** "2:15 PM" — the local wall-clock time of `dt`. Built by hand rather than
 *  through Intl: newer ICU puts a narrow no-break space before "PM", so the
 *  same moment would read differently on two machines (and in a test). */
export function fmtClockTime(dt: Date): string {
  const h = dt.getHours()
  const mm = String(dt.getMinutes()).padStart(2, '0')
  return `${h % 12 || 12}:${mm} ${h < 12 ? 'AM' : 'PM'}`
}

/** "Oct 7, 2:14 PM" — a local date and time, for "when did this happen". */
export function fmtMonthDayTime(dt: Date): string {
  return `${MONTHS[dt.getMonth()]} ${dt.getDate()}, ${fmtClockTime(dt)}`
}

/** "Dec 31, 2025" for a `YYYY-MM-DD` — for a date that may sit in another
 *  year than the one on screen (local calendar, never shifts a day). */
export function fmtMonthDayYear(ymd: string | null | undefined): string {
  const d = parseYMD(ymd)
  if (!d) return '—'
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}
